/**
 * The one way PropOS renders a time (ADR-39, docs/04 §7).
 *
 * Two kinds of value, and confusing them is a bug rather than a style slip:
 *
 * - An INSTANT (`audit_events.occurred_at`, `created_at`) is a moment on the
 *   clock. It must be converted to the reader's display timezone, because an
 *   event stored at 2026-08-22T01:00Z happened on Aug 21 in Charlotte and the
 *   ledger has to read the way the person who caused it remembers it.
 * - A CALENDAR DATE (a lease expiration, a COI expiry) is a date on a
 *   contract with no time and no timezone. Shifting it by an offset moves a
 *   legal date, so it is formatted without conversion.
 *
 * Both render inside <time datetime> so the machine-readable value survives
 * copy-paste and assistive technology, with relative time in the tooltip for
 * instants ("3h ago") rather than in the text, which would make a list of
 * events impossible to scan in order.
 */

import { formatCalendarDate, formatDate, formatDateTime, formatRelative } from '@propos/shared';
import type { ReactNode } from 'react';

/**
 * An instant — ISO 8601 with an offset, converted to the display timezone.
 *
 * `dateOnly` drops the clock time for columns where the day is the whole point
 * (an "Updated" cell in a dense list). It is still an INSTANT: the date is
 * taken in the display timezone, never by slicing the UTC string, because
 * 2026-08-22T01:00Z is still Aug 21 to a reader in Charlotte.
 */
export function Timestamp({
  value,
  className,
  dateOnly = false,
}: {
  readonly value: string;
  readonly className?: string;
  readonly dateOnly?: boolean;
}): ReactNode {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return <span className={className}>—</span>;
  return (
    <time
      className={className}
      dateTime={value}
      title={dateOnly ? formatDateTime(instant) : formatRelative(instant, new Date())}
    >
      {dateOnly ? formatDate(instant) : formatDateTime(instant)}
    </time>
  );
}

/** A calendar date — `YYYY-MM-DD`, rendered with no timezone conversion. */
export function DateStamp({
  value,
  className,
}: {
  readonly value: string;
  readonly className?: string;
}): ReactNode {
  // Defensive: an instant handed to the calendar formatter would throw, and a
  // detail page must not blank out because one field arrived in the other
  // shape. Trim to the date part and let a non-date fall through as an em dash.
  const date = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return <span className={className}>—</span>;
  return (
    <time className={className} dateTime={date}>
      {formatCalendarDate(date)}
    </time>
  );
}
